/**
 * JoinPhygital - plug-and-play category-aware join page.
 *
 * Logic:
 * - Read launch_control[category].launch_state
 *   - "live" → show subscription intake (paid signup)
 *   - "coming_soon" → show waitlist join form
 *   - "private" → show "Invite required" message
 * - Same UI shell, different fields/copy/pricing per category from DB.
 */
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinPhygitalPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const category = params.get("category") || "makeup";

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", city: "" });
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/join/category-config?category=${category}`);
      setConfig(r.data);
    } catch (e) {
      toast.error("Could not load category info");
    } finally { setLoading(false); }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  // Pilots / hosts must NEVER see the public launch-soon waitlist page. Their
  // current pilot onboarding stage lives in /me Hosting tile instead.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("session_token");
      if (!raw) return;
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${raw}` } }).then((r) => {
        const u = r.data || {};
        if (u.is_pilot || u.is_host || u.is_provider || u.plan === "legacy_pilot_free") {
          navigate("/me", { replace: true });
        }
      }).catch(() => {});
    } catch { /* unauthenticated visitor: keep on this page */ }
  }, [navigate]);

  const submit = async () => {
    if (!form.email || !form.first_name) { toast.error("Name and email required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/join/submit`, { ...form, category, mode: config?.mode });
      setDone(true);
    } catch (e) {
      toast.error("Could not submit: " + (e.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  }

  const mode = config?.mode || "waitlist";
  const catName = config?.display_name || (category.charAt(0).toUpperCase() + category.slice(1));

  if (done) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <Card className="bg-white/5 border-white/10 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold mb-2">{mode === "live" ? "Welcome to Phygital" : "You're on the list"}</h2>
            <p className="text-sm text-gray-300 mb-4">
              {config?.confirmation_copy || (mode === "live"
                ? `We just sent a sign-in link to ${form.email}. Welcome to ${catName} on Phygital.`
                : `Thanks for joining the ${catName} waitlist. We'll notify ${form.email} as soon as ${catName} opens up.`)}
            </p>
            <Button variant="outline" onClick={() => navigate("/")} data-testid="join-done-back">Back to home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-xl mx-auto pt-12">
        <div className="mb-8">
          <Badge variant="outline" className="text-[10px] border-white/20 bg-white/5 mb-3 capitalize">{catName}</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            {mode === "live"
              ? `Join Phygital for ${catName}`
              : mode === "private"
              ? `${catName} on Phygital is invite only`
              : `${catName} is launching soon`}
          </h1>
          <p className="text-sm text-gray-400">
            {config?.tagline || (mode === "live"
              ? `Capture every client's preferences and inspo before they walk in. Built for ${catName.toLowerCase()} pros.`
              : mode === "private"
              ? "If you have an invite link, use that. Otherwise, leave your email and we'll get in touch."
              : `We'll notify you the moment ${catName} opens up. Until then, you'll be one of the first to know.`)}
          </p>
        </div>

        {mode === "live" && config?.pricing && (
          <Card className="bg-white/5 border-white/10 mb-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Your plan</p>
                  <p className="text-lg font-semibold">{config.pricing.plan_name || `${catName} Solo`}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">${config.pricing.price_monthly || 49}<span className="text-xs font-normal text-gray-400">/mo</span></p>
                  {config.pricing.trial_days > 0 && <p className="text-[10px] text-green-400">{config.pricing.trial_days}-day free trial</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-6 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-400">First name</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="join-first-name" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Last name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="join-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-400">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="join-email" />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Phone (optional)</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="join-phone" />
            </div>
            <div>
              <Label className="text-xs text-gray-400">City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="join-city" />
            </div>

            <Button onClick={submit} disabled={submitting} className="w-full bg-white text-black hover:bg-gray-200 rounded-full mt-4" data-testid="join-submit">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : (mode === "live" ? <Sparkles className="w-3 h-3 mr-1" /> : <Lock className="w-3 h-3 mr-1" />)}
              {mode === "live" ? `Start your ${config?.pricing?.trial_days > 0 ? "free trial" : "subscription"}` : mode === "private" ? "Request access" : "Join the waitlist"}
            </Button>
            <p className="text-[10px] text-gray-500 text-center">
              By joining, you agree to our Terms and Privacy Policy.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
